// src/pages/HRDashboard.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./FeedbackPage.css";
import apiClient from "../utils/apiClient";
import SessionHandler from "../components/SessionHandler";

/* ================= HELPERS ================= */
const normalizeStatus = (status) =>
  (status || "").toString().trim().toUpperCase();

const HRDashboard = () => {
  const navigate = useNavigate();

  /* ================= STATE ================= */
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);
  const [totalLeaveCount, setTotalLeaveCount] = useState(0);
  const [pendingWFHCount, setPendingWFHCount] = useState(0);
  const [pendingResignationRequests, setPendingResignationRequests] = useState(0);
  const [pendingSalarySlipRequests, setPendingSalarySlipRequests] = useState(0);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [pendingCFLeaveCount, setPendingCFLeaveCount] = useState(0);

  /* ================= AUTO REFRESH ================= */
  useEffect(() => {
    // Initial load
    fetchDashboardData();

    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 30000);

    // Cleanup
    return () => clearInterval(interval);
  }, []);

  /* ================= FETCH DASHBOARD DATA ================= */
  const fetchDashboardData = async () => {
    try {
      /* ---------------- LEAVE REQUESTS ---------------- */
      const leaveData = await apiClient("/leaves/");
      const leaveList = Array.isArray(leaveData?.results)
        ? leaveData.results
        : Array.isArray(leaveData)
        ? leaveData
        : [];

      setPendingLeaveCount(
        leaveList.filter(
          (req) => normalizeStatus(req.status) === "PENDING"
        ).length
      );
      setTotalLeaveCount(leaveList.length);

      /* ---------------- WFH REQUESTS ---------------- */
      const wfhData = await apiClient("/wfh/requests/");
      const wfhList = Array.isArray(wfhData?.results)
        ? wfhData.results
        : Array.isArray(wfhData)
        ? wfhData
        : [];

      setPendingWFHCount(
        wfhList.filter(
          (req) => normalizeStatus(req.status) === "PENDING"
        ).length
      );

      /* ---------------- RESIGNATION REQUESTS ---------------- */
      const resData = await apiClient("/resignation/hr/");
      const resList = Array.isArray(resData?.results)
        ? resData.results
        : Array.isArray(resData)
        ? resData
        : [];

      setPendingResignationRequests(
        resList.filter(
          (req) => normalizeStatus(req.status) === "PENDING"
        ).length
      );

      /* ---------------- SALARY SLIP REQUESTS ---------------- */
      const salaryData = await apiClient("/salary-slip/list/");
      const salaryList = Array.isArray(salaryData?.results)
        ? salaryData.results
        : Array.isArray(salaryData)
        ? salaryData
        : [];

      setPendingSalarySlipRequests(
        salaryList.filter(
          (req) => normalizeStatus(req.status) === "PENDING"
        ).length
      );

      /* ---------------- EMPLOYEE COUNT ---------------- */
      const empData = await apiClient("/auth/allemployees/");
      const empList = Array.isArray(empData?.results)
        ? empData.results
        : Array.isArray(empData)
        ? empData
        : [];

      setEmployeeCount(empList.length);

      /* ---------------- CF LEAVE APPROVAL ---------------- */
      const cfData = await apiClient("/cfleaves/approval/");
      const cfList = Array.isArray(cfData?.results)
        ? cfData.results
        : Array.isArray(cfData)
        ? cfData
        : [];

      setPendingCFLeaveCount(
        cfList.filter(
          (req) => normalizeStatus(req.status) === "PENDING"
        ).length
      );
    } catch (err) {
      console.error("Error fetching HR dashboard data:", err);
    }
  };

  /* ================= DASHBOARD TILES ================= */
  const features = [
    { title: "Employees", count: employeeCount, color: "blue", path: "/dashboard/ManagerAcess/allemployees" },
    { title: "Leave Requests", count: pendingLeaveCount, color: "orange", path: "/dashboard/ManagerAcess/leaverequestmanager" },
    { title: "Leave History", count: totalLeaveCount, color: "green", path: "/dashboard/ManagerAcess/leavehistory" },
    { title: "Salary-Slip Request", count: pendingSalarySlipRequests, color: "red", path: "/dashboard/ManagerAcess/salarysliprequestreview" },
    { title: "WFH Requests", count: pendingWFHCount, color: "purple", path: "/dashboard/leave/wfhrequestsPage" },
    /* ✅ CF LEAVE TILE */
    { title: "CF Leave Approval", count: pendingCFLeaveCount, color: "pink", path: "/dashboard/ManagerAccess/cfleaveApproval" },
    { title: "Resignation", count: pendingResignationRequests, color: "pink", path: "/dashboard/ManagerAccess/hrresignationreviewPage" },
    { title: "Announcements", count: "→", color: "teal", path: "/dashboard/ManagerAcess/hrannoucementpage" },
    { title: "Monthly Roster", count: "→", color: "gray", path: "/dashboard/ManagerAccess/rosterpage" },
    { title: "Holidays", count: "→", color: "gray", path: "/dashboard/ManagerAcess/hrholidays" },
    { title: "Awards", count: "→", color: "gray", path: "/dashboard/ManagerAcess/Awards" },
  ];

  /* ================= UI ================= */
  return (
    <div className="dashboard-container">
      <h1 className="dashboard-title">HR Dashboard</h1>

      <div className="card-grid">
        {features.map((feature, index) => (
          <div
            key={index}
            className={`card card-${feature.color}`}
            onClick={() => navigate(feature.path)}
          >
            <h2>{feature.title}</h2>
            <p>{feature.count}</p>
          </div>
        ))}
      </div>

      {/* Global session expiry modal */}
      <SessionHandler />
    </div>
  );
};

export default HRDashboard;
